import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import { Wordmark } from "./components/Wordmark";
import { CookieBanner } from "./components/CookieBanner";

import AuthPage from "./pages/AuthPage";
import Onboarding from "./pages/Onboarding";
import Home from "./pages/Home";
import Answer from "./pages/Answer";
import Memories from "./pages/Memories";
import MemoryDetail from "./pages/MemoryDetail";
import MyStory from "./pages/MyStory";
import ChapterEditor from "./pages/ChapterEditor";
import People from "./pages/People";
import Timeline from "./pages/Timeline";
import Photos from "./pages/Photos";
import Book from "./pages/Book";
import Profile from "./pages/Profile";
import PaymentResult from "./pages/PaymentResult";
import WritingTips from "./pages/WritingTips";
import HelpRemember from "./pages/HelpRemember";
import Gift from "./pages/Gift";
import GiftSuccess from "./pages/GiftSuccess";
import Redeem from "./pages/Redeem";
import Demo from "./pages/Demo";
import Landing from "./pages/Landing";
import Family from "./pages/Family";
import Contribute from "./pages/Contribute";
import StartTrial from "./pages/StartTrial";
import Subscribe from "./pages/Subscribe";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import FAQ from "./pages/FAQ";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-cream">
      <div className="text-center animate-fade-in">
        <Wordmark size="text-3xl" />
        <div className="mt-3 text-subtle text-sm">Opening your story…</div>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user, checked } = useAuth();
  const location = useLocation();
  if (!checked || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (!user.onboarded && location.pathname !== "/onboarding")
    return <Navigate to="/onboarding" replace />;
  return children;
}

// Login required, but no onboarding/subscription gating (trial & paywall screens live here).
function RequireAuth({ children }) {
  const { user, checked } = useAuth();
  const location = useLocation();
  if (!checked || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

// Full gate for the app itself: trial -> onboarding -> subscription.
function AppGate({ children }) {
  const { user, checked } = useAuth();
  const location = useLocation();
  if (!checked || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  const gate = user?.access?.gate;
  if (gate === "needs_trial") return <Navigate to="/start-trial" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  if (gate === "expired") return <Navigate to="/subscribe" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, checked } = useAuth();
  if (!checked || user === null) return <FullLoader />;
  if (user) return <Navigate to="/home" replace />;
  return children;
}

function RootGate() {
  const { user, checked } = useAuth();
  if (!checked || user === null) return <FullLoader />;
  if (user) return <Navigate to="/home" replace />;
  return <Landing />;
}

// Fires a Meta Pixel PageView on client-side route changes (the base code in index.html
// already fires the first PageView on initial load, so we skip the very first render).
function PixelPageTracker() {
  const location = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (window.fbq && localStorage.getItem("cookie_consent_v2") === "accepted") window.fbq("track", "PageView");
  }, [location.pathname]);
  return null;
}


function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><AuthPage mode="login" /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><AuthPage mode="register" /></PublicOnly>} />
      <Route path="/forgot" element={<PublicOnly><AuthPage mode="forgot" /></PublicOnly>} />
      <Route path="/reset" element={<AuthPage mode="reset" />} />

      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/start-trial" element={<RequireAuth><StartTrial /></RequireAuth>} />
      <Route path="/subscribe" element={<RequireAuth><Subscribe /></RequireAuth>} />
      <Route path="/answer" element={<AppGate><Answer /></AppGate>} />
      <Route path="/payment/success" element={<RequireAuth><PaymentResult mode="success" /></RequireAuth>} />
      <Route path="/payment/cancel" element={<RequireAuth><PaymentResult mode="cancel" /></RequireAuth>} />

      <Route path="/" element={<RootGate />} />
      <Route path="/contribute/:token" element={<Contribute />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/gift" element={<Gift />} />
      <Route path="/gift/success" element={<GiftSuccess />} />
      <Route path="/redeem" element={<Redeem />} />
      <Route path="/demo" element={<Demo />} />

      <Route element={<AppGate><Layout /></AppGate>}>
        <Route path="/home" element={<Home />} />
        <Route path="/memories" element={<Memories />} />
        <Route path="/memories/:id" element={<MemoryDetail />} />
        <Route path="/story" element={<MyStory />} />
        <Route path="/story/:id" element={<ChapterEditor />} />
        <Route path="/people" element={<People />} />
        <Route path="/timeline" element={<Timeline />} />
        <Route path="/photos" element={<Photos />} />
        <Route path="/family" element={<Family />} />
        <Route path="/tips" element={<WritingTips />} />
        <Route path="/help" element={<FAQ embedded />} />
        <Route path="/remember" element={<HelpRemember />} />
        <Route path="/book" element={<Book />} />
        <Route path="/profile" element={<Profile />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App grain">
      <BrowserRouter>
        <AuthProvider>
          <PixelPageTracker />
          <AppRoutes />
          <CookieBanner />
          <Toaster position="top-right" richColors offset={72} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
